import type { LAYOUT } from 'vars/index'
import type { ICard } from '../Card'
import type { CardCorners, CardTypeT } from '../Card.h'

import { styled } from '@linaria/react'
import { BorderOuterSC } from 'atoms/BorderOuter'
import { FallbackImageSC } from 'atoms/FallbackImage'
import { IconSC, IconSizes } from 'atoms/Icon'
import { PosterSC } from 'atoms/ImagePoster'
import { LabelStyled } from 'atoms/Label'
import { OverlayLayoutStyled } from 'atoms/OverlayLayout'
import { TextBoxSC } from 'atoms/TextBox'
import { COLOR, CORNERS, FIXED_INDENTS, MEDIA, TRANSITIONS } from 'vars/index'

export const TopCardBaseSC = styled.div<{
  aspectRatio?: number
  type?: CardTypeT
}>``
export const TopTvSimpleCardBaseSC = styled(TopCardBaseSC)``
export const TopVodSimpleCardBaseSC = styled(TopCardBaseSC)``
export const TopEventSimpleCardBaseSC = styled(TopCardBaseSC)``

export const VideoPosterSC = styled.video``
export const ImageBackgroundSC = styled.div<{
  backgroundColorForLogo?: COLOR
  rounded?: boolean
}>`
  background-color: ${({ backgroundColorForLogo }) =>
    backgroundColorForLogo ? `var(${backgroundColorForLogo})` : 'none'};
  border-radius: ${({ rounded }) => (rounded ? '50%' : 0)};
`
export const LockedPosterSC = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  row-gap: var(${FIXED_INDENTS.INDENT_10});

  ${IconSC} {
    ${MEDIA.MAX_599} {
      font-size: ${IconSizes.large};
    }
  }
`

const PosterContainerBaseSC = styled.div<{
  overlayOnHoverEnabled: ICard['overlayOnHoverEnabled']
  posterHeight: ICard['posterHeight']
  posterWidth: ICard['posterWidth']
  backgroundColor?: ICard['backgroundColor']
  gradient?: ICard['gradient']
  locked?: ICard['locked']
  corners?: CardCorners
  backgroundColorHovered?: ICard['backgroundColorHovered']
}>``

export const BaseCardOverlaySC = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  transition: background-color var(${TRANSITIONS.LINEAR_100});
`

export const PosterContainerContentSC = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`

export const BaseCardSC = styled.div<{
  corners: CardCorners
  width?: string
  cardHeight?: string
  aspectRatio: number
  overlayOnHoverEnabled: ICard['overlayOnHoverEnabled']
  gridCard?: boolean
  selected?: boolean
  borderVisible?: boolean
  gradient?: ICard['gradient']
  transformScale?: ICard['transformScale']
  rented?: ICard['rented']
  blockedByAcl?: ICard['aclBlockedBy']
}>`
  z-index: 0;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  border-radius: ${({ corners }) => `var(${corners})`};
  width: ${({ width }) => (width ? `var(--cardWidth)` : '100%')};
  height: ${({ aspectRatio, gridCard }) =>
    gridCard ? `calc(var(--cardWidth) / ${aspectRatio})` : '100%'};
  transition:
    box-shadow var(${TRANSITIONS.LINEAR_100}),
    font-size var(${TRANSITIONS.LINEAR_100}),
    transform var(${TRANSITIONS.LINEAR_100});

  &::before {
    z-index: 1;
    top: 0;
    left: 0;
    content: '';
    position: absolute;
    height: 100%;
    width: fit-content;
    border-image-slice: 1;
    border-style: solid;
    border-width: 0 0 0 4px;
    border-image-source: var(${COLOR.ACCENT_PRIMARY_GRADIENT});
    color: var(${COLOR.ACCENT_PRIMARY_GRADIENT});
    display: ${({ selected, borderVisible }) =>
      selected && borderVisible ? 'block' : 'none'};
  }

  &::after {
    z-index: 1;
    top: 0;
    left: 0;
    content: '';
    position: absolute;
    height: 100%;
    width: fit-content;
    border-style: solid;
    border-width: 0 0 0 4px;
    border-color: var(${COLOR.SURFACE_OPACITY_DARK_70});
    display: none;
  }

  ${MEDIA.NO_TOUCH} {
    &:hover,
    ${TopCardBaseSC}:hover & {
      z-index: 2;
      transform: ${({ transformScale }) =>
        transformScale
          ? `scaleY(${transformScale}) scaleX(${transformScale})`
          : 'initial'};
    }
  }

  &.scale-up {
    font-size: 0.83em;
  }

  &.scale-up-110 {
    font-size: 0.9em;
  }

  ${BorderOuterSC} {
    position: absolute;
    top: var(${FIXED_INDENTS.INDENT_20});
    left: var(${FIXED_INDENTS.INDENT_20});
    z-index: 1;
    transition: opacity var(${TRANSITIONS.LINEAR_100});
    max-width: 100%;

    ${TextBoxSC} {
      display: block;
      white-space: nowrap;
    }
  }

  // FP-4260: recommendations on the content details page use square corners;
  // size and padding stay on LabelPurchaseBadge (24x24 paid / 33x24 rented).
  &[data-details-card] {
    ${BorderOuterSC} {
      border-radius: var(${CORNERS.CORNERS_0});

      ${LabelStyled} {
        border-radius: var(${CORNERS.CORNERS_0});
      }
    }
  }

  // Prevent blue highlighting of elements after click or tap on element
  // https://stackoverflow.com/questions/21003535/anyway-to-prevent-the-blue-highlighting-of-elements-in-chrome-when-clicking-quic
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;

  // Also need to prevent blue highlighting for newer versions of Chrome

  &:focus {
    outline: none;
  }

  ${MEDIA.NO_TOUCH} {
    &:hover,
    ${TopCardBaseSC}:hover & {
      ${PosterContainerBaseSC} {
        &:before {
          opacity: ${({ gradient }) => (gradient ? 0 : 1)};
        }
      }
    }
  }

  &:active {
  }
`

export const NewLabelSC = styled.div`
  position: absolute;
  right: var(${FIXED_INDENTS.INDENT_20});
  bottom: var(${FIXED_INDENTS.INDENT_20});
  z-index: 1;
`

export const PosterContainerSC = styled(PosterContainerBaseSC)`
  position: absolute;
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;
  display: flex;
  transition: background-color var(${TRANSITIONS.LINEAR_100});
  border-radius: ${({ corners }) => `var(${corners})`};

  background: ${({ backgroundColor }) =>
    backgroundColor ? `var(${backgroundColor})` : 'none'};

  ${PosterSC} {
    width: ${({ posterWidth }) => (posterWidth ? `${posterWidth}%` : '100%')};
    height: ${({ posterHeight }) =>
      posterHeight ? `${posterHeight}%` : '100%'};
    object-fit: ${({ posterWidth, posterHeight }) =>
      posterWidth && posterHeight ? 'contain' : 'cover'};
    max-width: ${({ posterWidth }) => (posterWidth ? 'initial' : '100%')};
    max-height: ${({ posterHeight }) => (posterHeight ? 'initial' : '100%')};
    margin: auto;
  }

  ${ImageBackgroundSC} {
    margin: auto;
    display: flex;
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    overflow: hidden;

    ${PosterSC} {
      object-fit: contain;
    }
  }

  ${VideoPosterSC} {
    object-fit: fill;
    height: 100%;
    width: 100%;
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
  }

  ${OverlayLayoutStyled} {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
    width: 100%;
    height: 100%;
  }

  ${FallbackImageSC} {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &:before {
    display: ${({ gradient }) => (gradient ? 'block' : 'none')};
    content: '';
    position: absolute;
    inset: 0;
    background-image: ${({ gradient }) => `var(${gradient})`};
    opacity: 1;
    transition: opacity var(${TRANSITIONS.LINEAR_100});
  }

  &:after {
    content: '';
    display: none;
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    z-index: 2;
    pointer-events: none;
    background: var(${COLOR.TRANSPARENT});
    transition: background var(${TRANSITIONS.LINEAR_100});

    ${MEDIA.NO_TOUCH} {
      ${TopCardBaseSC}:hover &, ${BaseCardSC}:hover & {
        background: ${({ backgroundColorHovered }) =>
          `var(${backgroundColorHovered})`};
      }
    }
  }

  ${MEDIA.NO_TOUCH} {
    &:after {
      display: ${({ overlayOnHoverEnabled }) =>
        overlayOnHoverEnabled ? 'block' : 'none'};
    }
  }
`

export const CardContentContainerSC = styled.div<{
  paddings?: FIXED_INDENTS[] | LAYOUT[]
  verticalGap?: FIXED_INDENTS
  verticalAligning?: string
}>`
  position: absolute;
  z-index: 4;
  display: flex;
  justify-content: ${({ verticalAligning }) => verticalAligning || 'center'};
  flex-direction: column;
  inset: 0;
  transition: var(${TRANSITIONS.LINEAR_100});
  row-gap: ${({ verticalGap = FIXED_INDENTS.INDENT_0 }) =>
    `var(${verticalGap})`};
  padding: ${({ paddings = [FIXED_INDENTS.INDENT_0] }) =>
    paddings.map((padding) => `var(${padding})`).join(' ')};
`

export const BundlePosterContainerSC = styled(PosterContainerSC)``

export const CardPosterBackgroundCoverSC = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  img {
    width: 100%;
    height: 100%;
  }

  div {
    width: 100%;
    height: 100%;
    background-color: red;
  }
`

export const CardChildContentSC = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 100;

  & > * {
    ${TopCardBaseSC}:hover & {
      opacity: 1;
    }
  }
`
